import React, { ComponentType } from 'react';
import './desktopNavigation.module.scss';
import { HeaderLogo } from '../../components/atoms/Logo';
import { inject, observer } from 'mobx-react';
import { FlagKeys } from '../../../mainv2/states/CurrentUserState';
import classNames from 'classnames';
import { EnhancedLocationDescriptor, V3Routes } from '../../routes';
import { Link } from 'react-router-dom';
import { routingStore } from '../../../common/stores/routing';
import { GlobalStatesV3 } from '../../../commonv3/states/GlobalStatesV3';
import { IconHome } from './IconHome';
import { IconCourse } from './IconCourse';
import { HomeNavigationBackground } from '../../states/NavigationState';
import { observable, makeObservable } from 'mobx';
import { DesktopNavigationState } from './states/DesktopNavigationState';
import { CoachSkillCertificationState } from '../../states/CoachSkillCertificationState';
import { IconTimesheet } from './IconTimesheet';
import { NotificationIcon } from './NotificationIcon';
import { Create } from '@material-ui/icons';
import { IconCommunity } from './IconCommunity';
import { DesktopUserInfo } from '../../../commonv3/components/organisms/navigation/DesktopUserInfo';
import { IconEvent } from './IconEvent';

type PopupMenuItemProps = {
  to: EnhancedLocationDescriptor;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
  openInNewTab?: boolean;
};

const PopupMenuItem = observer(({ to, children, openInNewTab = false }: PopupMenuItemProps) => {
  return (
    <div
      styleName="menu-item"
      onClick={() => (openInNewTab ? window.open(to.pathname, '_blank') : routingStore.push(to))}
    >
      <span styleName="menu-item-text">{children}</span>
    </div>
  );
});

type PopupMenuProps = {
  show: boolean;
  onMouseLeave: () => void;
};

const PopupMenu = observer(({ show, onMouseLeave }: PopupMenuProps) => {
  const currentUser = GlobalStatesV3.currentUser;
  return (
    <div id="popup-menu" styleName={classNames('popup-menu', { show: show })} onMouseLeave={onMouseLeave}>
      <div styleName="hover-area" />
      <PopupMenuItem to={V3Routes.profile()}>プロフィールを編集</PopupMenuItem>
      <PopupMenuItem to={V3Routes.reservations()}>予約の確認</PopupMenuItem>
      <div styleName="separator" />
      <PopupMenuItem to={V3Routes.plan()}>プランの確認 / 変更</PopupMenuItem>
      {!currentUser.isMultiCreatorUser() && (
        <PopupMenuItem to={V3Routes.multiCreators()}>コースのアップグレード</PopupMenuItem>
      )}
      <PopupMenuItem to={V3Routes.address()}>住所の確認</PopupMenuItem>
      <PopupMenuItem to={V3Routes.payments()}>お支払い情報 / 変更</PopupMenuItem>
      <PopupMenuItem to={V3Routes.homeLocation()}>ホーム拠点の確認 / 変更</PopupMenuItem>
      <PopupMenuItem to={V3Routes.password()}>パスワードを変更</PopupMenuItem>
      <PopupMenuItem to={V3Routes.email()}>メールの設定</PopupMenuItem>
      <PopupMenuItem to={V3Routes.faq()} openInNewTab>
        よくあるご質問
      </PopupMenuItem>
      <PopupMenuItem to={V3Routes.signOut()}>ログアウト</PopupMenuItem>
    </div>
  );
});

type NavItemProps = {
  id?: string;
  to?: EnhancedLocationDescriptor;
  href?: string;
  activeWhen: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
};

const NavItem = observer(({ id, to, href, activeWhen, children }: NavItemProps) => {
  const active = routingStore.location.pathname.match(activeWhen);
  if (href) {
    return (
      <a styleName={classNames('nav-item', { active: active })} href={href} id={id}>
        <div styleName="nav-item-inner">{children}</div>
      </a>
    );
  }
  if (to) {
    return (
      <Link styleName={classNames('nav-item', { active: active })} to={to} id={id}>
        <div styleName="nav-item-inner">{children}</div>
      </Link>
    );
  }
  return null;
});

type InternalProps = {
  coachSkillState: CoachSkillCertificationState;
};

type Props = {
  background: HomeNavigationBackground;
};

const DesktopNavigationComponent = inject('coachSkillState')(
  observer(
    class DesktopNavigationComponent extends React.Component<InternalProps & Props> {
      pageState = new DesktopNavigationState();

      constructor(props: InternalProps & Props) {
        super(props);

        makeObservable(this, {
          pageState: observable,
        });
      }

      render() {
        const { background, coachSkillState } = this.props;
        const { showPopup } = this.pageState;
        const currentUser = GlobalStatesV3.currentUser;
        return (
          <div
            styleName={classNames('nav-wrapper', background, { 'has-any-skills': coachSkillState.hasAnySkill })}
            data-testid="desktop-home-navigation"
          >
            <div styleName="nav">
              <HeaderLogo onClick={() => GlobalStatesV3.routing.push('/')} />
              <div styleName="nav-main-wrap">
                <div styleName="nav-main">
                  <NavItem to={V3Routes.home()} activeWhen="^/?$|^/home/?$|^/home/reservations?$|^/home/?">
                    <IconHome styleName="icon" />
                    ホーム
                  </NavItem>
                  <NavItem to={V3Routes.courses()} activeWhen="/courses/?">
                    <IconCourse styleName="icon" />
                    コース一覧
                  </NavItem>
                  <NavItem
                    to={V3Routes.userCourseWorkSubmissions()}
                    activeWhen="^/course_work_submissions?|/lesson_homework?|/exam_?"
                  >
                    <Create styleName="icon" /> 課題
                  </NavItem>
                  {currentUser.hasEnabledFlagKey(FlagKeys.COMMUNITY_SERVICE) && (
                    <NavItem
                      href={V3Routes.communityService()}
                      activeWhen="^/__disabled__?"
                      id="nav-item-community-service"
                    >
                      <IconCommunity styleName="icon community" /> SHEコミュニティ
                    </NavItem>
                  )}
                  <NavItem to={V3Routes.events('event_tab')} activeWhen="/events/?">
                    <IconEvent styleName="icon" /> イベント
                  </NavItem>
                  {coachSkillState.hasAnySkill && (
                    <NavItem to={V3Routes.timesheetHome()} activeWhen="/timesheet/?">
                      <IconTimesheet styleName="icon" /> シフト
                    </NavItem>
                  )}
                  {coachSkillState.hasAnySkill && (
                    <NavItem to={V3Routes.courseWorkSubmissions()} activeWhen="/reviewer/?">
                      <Create styleName="icon" /> 課題添削
                    </NavItem>
                  )}
                </div>
                <div styleName="user-info-wrap">
                  <NotificationIcon />
                  <DesktopUserInfo
                    currentUser={currentUser}
                    active={this.pageState.showPopup}
                    onMouseEnter={() => this.pageState.openPopup()}
                  />
                </div>
              </div>
              <PopupMenu show={showPopup} onMouseLeave={() => this.pageState.closePopup()} />
            </div>
          </div>
        );
      }
    }
  )
);

export const DesktopNavigation = DesktopNavigationComponent as ComponentType<Props>;
